/**
 * **E01 · Print tokens** (Stream E, Rebuild 39's DesignSystem.dc.html): every sheet the Paper
 * family prints reads its size, margins, type scale, rules, mark, footer and QR from here, and
 * `sheet.css` carries the same numbers (its test holds them equal). Sizes are at 72 px to the
 * inch, so a point on the page is a pixel in the drawing. Black-and-white safe: on paper a chip is
 * the word in a box, and red is only a gap or an over, with the word printed beside it.
 */

export const PX_PER_INCH = 72;

/** The seven sheets: letter, half letter, the 4×6 card both ways, the 3×4 badge, the tag, 11×17. */
export const SHEET_SIZES = [
  'letter',
  'half',
  'card',
  'card-landscape',
  'badge',
  'tag',
  'poster',
  // Print's slide family (Stream D, James Oct 18): a 16:9 slide, one figure and one sentence.
  'slide',
] as const;
export type SheetSize = (typeof SHEET_SIZES)[number];

export interface SheetSpec {
  /** In points (= px at 72 / in). */
  width: number;
  height: number;
  margin: { top: number; side: number; bottom: number };
  /** title · sub · section head · row · the big line. Never below 8 pt on paper. */
  type: { title: number; sub: number; head: number; row: number; big: number };
  /** The header rule under the masthead. */
  rule: number;
  /** The mark's height, top-left. */
  mark: number;
}

const inch = (n: number): number => Math.round(n * PX_PER_INCH * 100) / 100;
const LETTER_MARGIN = { top: inch(0.5), side: inch(0.5), bottom: inch(0.36) };
const LETTER_TYPE = { title: 17, sub: 10.5, head: 10, row: 11.5, big: 34 };
const CARD_TYPE = { title: 12, sub: 9, head: 8.5, row: 10, big: 28 };
const BADGE_TYPE = { title: 9, sub: 8, head: 8, row: 9.5, big: 30 };
const card = { top: inch(0.28), side: inch(0.28), bottom: inch(0.28) };
const badge = { top: inch(0.2), side: inch(0.2), bottom: inch(0.2) };

export const SHEET: Record<SheetSize, SheetSpec> = {
  letter: { width: 612, height: 792, margin: LETTER_MARGIN, type: LETTER_TYPE, rule: 2, mark: 34 },
  half: { width: 612, height: 396, margin: LETTER_MARGIN, type: LETTER_TYPE, rule: 2, mark: 34 },
  card: { width: 288, height: 432, margin: card, type: CARD_TYPE, rule: 1.5, mark: 26 },
  'card-landscape': { width: 432, height: 288, margin: card, type: CARD_TYPE, rule: 1.5, mark: 26 },
  badge: { width: 216, height: 288, margin: badge, type: BADGE_TYPE, rule: 1.5, mark: 22 },
  tag: { width: 216, height: 144, margin: badge, type: BADGE_TYPE, rule: 1.5, mark: 22 },
  poster: {
    width: 792,
    height: 1224,
    margin: { top: inch(0.4), side: inch(0.4), bottom: inch(0.4) },
    type: { title: 18, sub: 10, head: 9.5, row: 10.5, big: 42 },
    rule: 2,
    mark: 34,
  },
  slide: {
    width: 960,
    height: 540,
    margin: { top: inch(0.5), side: inch(0.6), bottom: inch(0.36) },
    type: { title: 30, sub: 16, head: 14, row: 18, big: 72 },
    rule: 3,
    mark: 40,
  },
};

/** The smallest type on paper, the footer aside. */
export const PAPER_MIN_PT = 8;
/** The footer: DM Mono 7.5 — the sheet's name and purpose left, "CIDER · printed …" right. */
export const FOOTER_PT = 7.5;
/** The QR block, top-right, its code under it: the only thing CIDER reads back, one a sheet. */
export const QR_PX = { min: 48, max: 58 } as const;
/** The section head's rule and a row's (dotted), in px. */
export const SECTION_RULE = 1;
export const ROW_RULE = 1;
/** The footer's right-hand words. */
export const printedLine = (when: string): string => `CIDER · printed ${when}`;
